import { useQueryClient } from '@tanstack/react-query';

import { ResultListModel } from '@common-lib';

import {
  StampCardListModel,
  stampCardClient,
} from '@portal-clients/stamp-card';

import { useInfiniteScrollClientQuery } from '@frontend/hooks';
import { useLicense } from '@frontend/licenses';

const fetchStampCards = async (pageToken: string | undefined) => {
  return stampCardClient.list({
    pageSize: 20,
    pageToken: pageToken,
    sortBy: {
      field: 'CREATED_AT',
      order: 'DESC',
    },
    filter: {
      ids: undefined,
      validityStatus: 'ACTIVE',
    },
  });
};

export interface UseStampCardProps {
  onError?(): void;
}

export interface UseStampCardBySiteUnitProps {
  siteUnitId: string;
  onError?(): void;
}

export const getStampCardQueryKey = () => ['stamp-cards'];

export const useStampCardQuery = ({ onError }: UseStampCardProps = {}) => {
  const hasLicense = useLicense('stampCards');

  return useInfiniteScrollClientQuery({
    queryKey: getStampCardQueryKey(),
    enabled: hasLicense,
    onFetch: async (pageToken) => {
      return await fetchStampCards(pageToken);
    },
    onError,
  });
};

export const usePrefetchStampCardsQuery = () => {
  const queryClient = useQueryClient();
  const hasLicense = useLicense('stampCards');

  if (!hasLicense) {
    return;
  }

  queryClient.prefetchInfiniteQuery({
    pages: 1,
    queryKey: getStampCardQueryKey(),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: ResultListModel<StampCardListModel>) => {
      return lastPage.nextPageToken;
    },
    queryFn: async ({ pageParam: pageToken }) => {
      const response = await fetchStampCards(pageToken);
      return response;
    },
  });
};
